import type { FunctionComponent } from 'react';
import { useMemo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { Footer } from '../../../footer/containers/footer';
import { CompactNavigationBar } from '../../../gameplay/components/compact-navigation-bar/compact-navigation-bar';
import type { MetaTagsDescriptor } from '../../../shared/components/meta/meta';
import { Meta } from '../../../shared/components/meta/meta';
import { Toaster } from '../../../shared/components/toaster/toaster';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { useContextNavigate } from '../../../shared/hooks/use-context-navigate';
import { layoutStore, selectIsMainMenuOpen } from '../../../shared/store/layout.store';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { TopNavigation } from '../../components/top-navigation/top-navigation';
import { LargeTopNavigation } from '../../containers/large-top-navigation/large-top-navigation';
import { SideNavigationContainer } from '../../containers/side-navigation/side-navigation';
import { isInApp } from '../../util/is-app';
import { useLayoutContext } from '../../util/layout-context';

import styles from './main.module.scss';

const style = createStyleHelper(styles, 'app');

export const Main: FunctionComponent = () => {
    const { layout } = useLayoutContext();
    const intl = useIntl();
    const isDesktop = useDesktop();
    const isMenuOpen = useSelector(selectIsMainMenuOpen);
    const dispatch = useDispatch();
    const toggleMenu = useCallback(
        () => dispatch(layoutStore.actions.toggleMainMenu()),
        [dispatch],
    );
    const isNativeApp = isInApp();
    const [googlePlay, setGooglePlay] = useState(false);

    useEffect(() => {
        // Stop the body from being scrollable while menus are open.
        if (isMenuOpen) {
            document.body.classList.add('overflow-hidden');
            return;
        }

        document.body.classList.remove('overflow-hidden');
    }, [isMenuOpen]);

    useEffect(() => {
        if (isMenuOpen && isDesktop) {
            toggleMenu();
        }
    }, [toggleMenu, isDesktop, isMenuOpen]);

    useEffect(() => {
        if (isInApp()) {
            document.body.classList.add('native');
        }
    }, []);

    const navigate = useContextNavigate();
    const onCloseNav = useCallback(() => {
        navigate('/');
    }, [navigate]);

    const metaTags = useMemo(
        (): MetaTagsDescriptor[] => [
            {
                tag: 'title',
                content: intl.formatMessage({
                    id: 'page.title',
                    defaultMessage: 'Crab Sports',
                }),
            },
        ],
        [intl],
    );

    const metaTagsDescription = useMemo(
        (): MetaTagsDescriptor[] => [
            {
                tag: 'meta',
                name: 'description',
                content: intl.formatMessage({
                    id: 'page.description',
                    defaultMessage:
                        'Sign up now to Crab Sports, Maryland’s #1 Local Sportsbook. Boosted odds, DMV parlays and great promos. Safe, secure and fully regulated. Deposit now!',
                }),
            },
        ],
        [intl],
    );

    const metaTagsTitle = useMemo(
        (): MetaTagsDescriptor[] => [
            {
                tag: 'script',
                type: 'application/ld+json',
                content: JSON.stringify({
                    '@context': 'https://schema.org/',
                    '@type': 'WebSite',
                    'name': 'Crab Sports',
                    'description':
                        'Sign up now to Crab Sports, Maryland’s #1 Local Sportsbook. Boosted odds, DMV parlays and great promos. Safe, secure and fully regulated. Deposit now!',
                }),
            },
        ],
        [intl],
    );

    useEffect(() => {
        const handleBeforeInstallPrompt = (e: any) => {
            // Prevent the default browser install prompt

            setGooglePlay(true);
            e.preventDefault();

            // Store the event for later use
            const deferredPrompt = e;
            setGooglePlay(true);
            console.log('NEXT', e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            // Remove the event listener when the component unmounts
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    return (
        <>
            <div
                className={style(undefined, {
                    'highlight-content': isDesktop,
                    'alternate-background': !!layout.showMobileBackground && !isDesktop,
                    'fixed-height': !!layout.isFixedHeight,
                })}
            >
                <Meta tags={metaTags} />
                <Meta tags={metaTagsDescription} />
                <Meta tags={metaTagsTitle} />
                <header className={style('header')}>
                    {!isNativeApp ? (
                        <>
                            <SideNavigationContainer
                                className={style('side-navigation', { visibile: isMenuOpen })}
                                onCloseNav={toggleMenu}
                            />
                            {isDesktop ? (
                                <LargeTopNavigation
                                    hideNavigationActions={!layout.showNavButtons}
                                />
                            ) : layout.title ? (
                                <CompactNavigationBar
                                    onClose={onCloseNav}
                                    title={<FormattedMessage id={layout.title} />}
                                />
                            ) : (
                                <TopNavigation
                                    showSearch={layout?.showSearch}
                                    onToggleMenu={toggleMenu}
                                />
                            )}
                        </>
                    ) : null}
                    {/*{!isDesktop ? <AccountActions /> : null}*/}
                </header>
                <div
                    className={style('content', {
                        'full': !!layout.isFullWidth,
                        'native': isNativeApp,
                        'fixed-height': !!layout.isFixedHeight,
                    })}
                >
                    <Toaster className={style('toaster')} />
                    <Outlet />
                </div>
            </div>
            {!isNativeApp ? <Footer className={style('footer')} /> : null}
        </>
    );
};
